import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import { FaLinkedinIn, FaFacebookF, FaInstagram } from 'react-icons/fa';

import { primaryNav } from '../../data/navigation';
import { company } from '../../data/company';
import logo from '../../assets/img/bethel-logo.png';
import './header.css';

const SOCIAL_ICONS = {
  LinkedIn: FaLinkedinIn,
  Facebook: FaFacebookF,
  Instagram: FaInstagram,
};

/* Home is reachable from the logo and the footer, so it is omitted from the
   desktop bar to keep six items from crowding at 1024px. It stays in the
   mobile drawer, where vertical space is not the constraint. */
const desktopNav = primaryNav.filter(
  (item) => item.label !== 'Home' && item.label !== 'Contact'
);

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navRef = useRef(null);
  const toggleRef = useRef(null);

  /* Close everything on route change — otherwise the drawer stays open over
     the new page after a tap. */
  useEffect(() => {
    setDrawerOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  /* Compact the header once the page scrolls. */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Escape closes the open submenu, then the drawer — and returns focus to
     the control that opened it (WCAG 2.1.2, no keyboard trap). */
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Escape') return;
      if (openMenu) {
        setOpenMenu(null);
      } else if (drawerOpen) {
        setDrawerOpen(false);
        toggleRef.current?.focus();
      }
    },
    [openMenu, drawerOpen]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /* Pointer outside the nav closes an open submenu. */
  useEffect(() => {
    if (!openMenu) return undefined;
    const onPointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [openMenu]);

  /* Lock body scroll behind the mobile drawer. */
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  const isDesktop = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(min-width: 1024px)').matches;

  const renderSubmenu = (item, idPrefix) => (
    <ul id={`${idPrefix}-${item.label}`} className="nav__submenu">
      {item.children.map((child) => (
        <li key={child.to}>
          <NavLink
            to={child.to}
            className={({ isActive }) =>
              `nav__sublink${isActive ? ' is-active' : ''}`
            }
          >
            {child.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );

  return (
    <header className={`site-header${scrolled ? ' is-scrolled' : ''}`}>
      <div className="container site-header__inner">
        <Link to="/" className="site-header__brand" aria-label="Bethel Innovations — home">
          <img src={logo} alt="Bethel Innovations" width="1057" height="236" />
        </Link>

        {/* ---------------- Desktop navigation ---------------- */}
        <nav className="nav-desktop" aria-label="Primary" ref={navRef}>
          <ul className="nav__list">
            {desktopNav.map((item) => {
              const hasChildren = Boolean(item.children);
              const isOpen = openMenu === item.label;

              return (
                <li
                  key={item.to}
                  className={`nav__item${hasChildren ? ' nav__item--has-menu' : ''}`}
                  onMouseEnter={() => hasChildren && isDesktop() && setOpenMenu(item.label)}
                  onMouseLeave={() => hasChildren && isDesktop() && setOpenMenu(null)}
                  onBlur={(event) => {
                    if (
                      hasChildren &&
                      !event.currentTarget.contains(event.relatedTarget)
                    ) {
                      setOpenMenu(null);
                    }
                  }}
                >
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `nav__link${isActive ? ' is-active' : ''}`
                    }
                    end={item.to === '/'}
                  >
                    {item.label}
                  </NavLink>

                  {hasChildren && (
                    <>
                      {/* A real button, so the submenu is reachable by keyboard.
                          The previous site opened this on :hover only. */}
                      <button
                        type="button"
                        className="nav__disclosure"
                        aria-expanded={isOpen}
                        aria-controls={`desktop-${item.label}`}
                        onClick={() => setOpenMenu(isOpen ? null : item.label)}
                      >
                        <FiChevronDown aria-hidden="true" />
                        <span className="sr-only">
                          {isOpen ? 'Hide' : 'Show'} {item.label} submenu
                        </span>
                      </button>
                      <div className={`nav__dropdown${isOpen ? ' is-open' : ''}`}>
                        {renderSubmenu(item, 'desktop')}
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* ---------------- Utilities ---------------- */}
        <div className="site-header__utils">
          <ul className="site-header__social">
            {company.social.map(({ name, href }) => {
              const Icon = SOCIAL_ICONS[name];
              return (
                <li key={name}>
                  {/* aria-label belongs on the anchor, not the icon — the
                      previous markup left these links with no accessible name. */}
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Bethel Innovations on ${name} (opens in a new tab)`}
                  >
                    <Icon aria-hidden="true" focusable="false" />
                  </a>
                </li>
              );
            })}
          </ul>

          <Link to="/contact-us" className="btn btn--primary btn--sm site-header__cta">
            Contact Us
          </Link>

          <button
            type="button"
            ref={toggleRef}
            className="site-header__toggle"
            aria-expanded={drawerOpen}
            aria-controls="mobile-drawer"
            onClick={() => setDrawerOpen((open) => !open)}
          >
            {drawerOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
            <span className="sr-only">
              {drawerOpen ? 'Close navigation menu' : 'Open navigation menu'}
            </span>
          </button>
        </div>
      </div>

      {/* ---------------- Mobile drawer ---------------- */}
      <div
        className={`drawer-backdrop${drawerOpen ? ' is-open' : ''}`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />
      <nav
        id="mobile-drawer"
        className={`drawer${drawerOpen ? ' is-open' : ''}`}
        aria-label="Primary"
        hidden={!drawerOpen}
      >
        <ul className="drawer__list">
          {primaryNav.map((item) => {
            const hasChildren = Boolean(item.children);
            const isOpen = openMenu === `m-${item.label}`;

            return (
              <li key={item.to} className="drawer__item">
                <div className="drawer__row">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `drawer__link${isActive ? ' is-active' : ''}`
                    }
                    end={item.to === '/'}
                  >
                    {item.label}
                  </NavLink>

                  {hasChildren && (
                    <button
                      type="button"
                      className={`drawer__disclosure${isOpen ? ' is-open' : ''}`}
                      aria-expanded={isOpen}
                      aria-controls={`mobile-${item.label}`}
                      onClick={() => setOpenMenu(isOpen ? null : `m-${item.label}`)}
                    >
                      <FiChevronDown aria-hidden="true" />
                      <span className="sr-only">
                        {isOpen ? 'Hide' : 'Show'} {item.label} submenu
                      </span>
                    </button>
                  )}
                </div>

                {hasChildren && isOpen && (
                  <ul id={`mobile-${item.label}`} className="drawer__submenu">
                    {item.children.map((child) => (
                      <li key={child.to}>
                        <NavLink
                          to={child.to}
                          className={({ isActive }) =>
                            `drawer__sublink${isActive ? ' is-active' : ''}`
                          }
                        >
                          {child.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        <div className="drawer__footer">
          <a href={company.contact.phoneHref} className="drawer__contact">
            {company.contact.phone}
          </a>
          <a href={company.contact.emailHref} className="drawer__contact">
            {company.contact.email}
          </a>
          <ul className="drawer__social">
            {company.social.map(({ name, href }) => {
              const Icon = SOCIAL_ICONS[name];
              return (
                <li key={name}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Bethel Innovations on ${name} (opens in a new tab)`}
                  >
                    <Icon aria-hidden="true" focusable="false" />
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Header;
